import { useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { handleAnchorClick } from "@/lib/scroll";
import AvailabilityBanner from "./AvailabilityBanner";

const LINKS = [
    { label: "Home", href: "#home", testId: "nav-link-home" },
    { label: "Services", href: "#services", testId: "nav-link-services" },
    { label: "Pricing", href: "#pricing", testId: "nav-link-pricing" },
    { label: "About", href: "#about", testId: "nav-link-about" },
    { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

const Nav = () => {
    const [open, setOpen] = useState(false);

    return (
        <header
            data-testid="site-nav"
            className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/85 backdrop-blur-md"
        >
            <AvailabilityBanner />
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <a
                    href="#home"
                    data-testid="nav-brand"
                    onClick={(e) => handleAnchorClick(e, "#home", () => setOpen(false))}
                    className="group flex items-center gap-2.5"
                >
                    <img
                        src="/logo-mark.png"
                        alt="CS Studio logo"
                        className="h-9 w-9 object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="text-[15px] font-bold tracking-tight text-ink">
                        CS Studio
                    </span>
                </a>

                <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
                    {LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            data-testid={link.testId}
                            onClick={(e) => handleAnchorClick(e, link.href)}
                            className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:bg-ice hover:text-ink"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="#contact"
                        data-testid="nav-cta-start-project"
                        onClick={(e) => handleAnchorClick(e, "#contact", () => setOpen(false))}
                        className="group hidden items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark sm:inline-flex"
                    >
                        Start a Project
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <button
                        type="button"
                        data-testid="nav-mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink transition-colors duration-200 hover:bg-ice md:hidden"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="border-t border-slate-200/80 bg-white px-4 pb-6 pt-3 md:hidden"
                >
                    <nav className="flex flex-col gap-1" aria-label="Mobile">
                        {LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                data-testid={`${link.testId}-mobile`}
                                onClick={(e) => handleAnchorClick(e, link.href, () => setOpen(false))}
                                className="rounded-xl px-4 py-3 text-base font-medium text-slate-700 transition-colors duration-200 hover:bg-ice hover:text-ink"
                            >
                                {link.label}
                            </a>
                        ))}
                        <a
                            href="#contact"
                            data-testid="nav-cta-start-project-mobile"
                            onClick={(e) => handleAnchorClick(e, "#contact", () => setOpen(false))}
                            className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
                        >
                            Start a Project
                            <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Nav;
